import React, { PureComponent, Component } from "react";
import styled, { keyframes, css } from "styled-components";
import NumberFormat from "react-number-format";
import Fragment from "react-dot-fragment";
import { putRequest } from "../../../api/ApiService";
import { incrementUnacceptedDecisions } from "../../../api/SubmitDecisions";
import { logError } from "../../../services/utils";
import EventEmitter from "../../../services/event-emitter";
import Auth from "../../../routes/Auth";
import Teams from "../../../api/Teams";
import ErrorAlert from "../../alerts/error";
import SuccessAlert from "../../alerts/success";
import FinanceStyled from "../../finance/style";

// Trailing slash matters: without it Django responds 301 → /bulk/,
// and the browser-side PUT redirect drops the request body which then
// 500s server-side.
const PROMOS_BULK_URL = `${process.env.REACT_APP_API_URL}team/promos/bulk/`;

const FONT = "'Mukta Vaani', Lato, sans-serif";

// Per-channel slider cap. Channels can hold up to $5,000,000 each.
const PER_CHANNEL_MAX = 5000000;
const SLIDER_STEP = 5000;

const CATEGORY_DIGITAL = 2;

// Name → description / icon map. Names match what the backend returns
// in marketing_clients. Unknown names fall back to a generic icon and
// the name as the description.
const CHANNEL_META = {
  "Trade Shows & Events": {
    description: "Face-to-face engagement with high-intent buyers",
    iconKey: "tradeshow",
    explainer: "Industry expos where you set up a booth and meet potential buyers face-to-face. Great for demoing your product to serious customers.",
  },
  "TV & Print Ads": {
    description: "Mass market reach with strong brand recall",
    iconKey: "tv",
    explainer: "Broadcast commercials and print ads in newspapers or magazines. Reaches a huge audience and builds strong brand recognition over time.",
  },
  "Direct Mail": {
    description: "Targeted physical outreach with high demographic precision",
    iconKey: "mail",
    explainer: "Postcards, catalogs, and letters sent to specific mailing lists so you can zero in on the neighborhoods and households that fit your buyer.",
  },
  "Sponsorships": {
    description: "Associates your brand with events your audience already loves",
    iconKey: "sponsorship",
    explainer: "Paying to attach your brand to an event, team, or cause your audience already loves, so their goodwill rubs off on you.",
  },
  "In-Store / POS": {
    description: "Drives conversion at the point of purchase",
    iconKey: "store",
    explainer: "Displays, signage, and product placement inside retail stores that catch shoppers right when they're about to make a purchase.",
  },
  "Content Marketing": {
    description: "Builds long-term organic visibility and trust",
    iconKey: "content",
    explainer: "Blog posts, articles, and videos your team publishes to educate customers and pull them in through search over time.",
  },
  "Paid Digital Ads": {
    description: "Fast, targeted reach with measurable conversion",
    iconKey: "ads",
    explainer: "Ads on Google, Facebook, Instagram, and similar platforms. You pick who sees them and can measure exactly who clicks and buys.",
  },
  "Influencer Marketing": {
    description: "Authentic social proof with younger audiences",
    iconKey: "influencer",
    explainer: "Partnering with technical experts, industrial vision podcasters, and academic innovators whose audiences trust their recommendations.",
  },
  "Email Campaigns": {
    description: "Direct nurture channel with high conversion rates",
    iconKey: "email",
    explainer: "Regular emails sent to your prospects and customers, delivered straight to their inbox. High conversion rate for every dollar spent.",
  },
};

// Inline SVG icons keyed by CHANNEL_META.iconKey. Stroke icons sized
// 22x22, currentColor — match the design HTML.
const renderChannelIcon = (key) => {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 2,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    width: 22,
    height: 22,
  };
  switch (key) {
    case "tradeshow":
      return (
        <svg {...common}>
          <circle cx="9" cy="7" r="4" />
          <path d="M3 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
          <circle cx="17" cy="7" r="3" />
          <path d="M20 21v-1.5a3 3 0 0 0-3-3" />
        </svg>
      );
    case "tv":
      return (
        <svg {...common}>
          <rect x="2" y="5" width="20" height="14" rx="2" />
          <line x1="8" y1="22" x2="16" y2="22" />
          <line x1="12" y1="19" x2="12" y2="22" />
        </svg>
      );
    case "mail":
      return (
        <svg {...common}>
          <line x1="22" y1="2" x2="11" y2="13" />
          <polygon points="22 2 15 22 11 13 2 9 22 2" />
        </svg>
      );
    case "sponsorship":
      return (
        <svg {...common}>
          <circle cx="12" cy="8" r="7" />
          <polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88" />
        </svg>
      );
    case "store":
      return (
        <svg {...common}>
          <path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4Z" />
          <path d="M3 6h18" />
          <path d="M16 10a4 4 0 0 1-8 0" />
        </svg>
      );
    case "content":
      return (
        <svg {...common}>
          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
          <polyline points="14 2 14 8 20 8" />
          <line x1="16" y1="13" x2="8" y2="13" />
          <line x1="16" y1="17" x2="8" y2="17" />
          <line x1="10" y1="9" x2="8" y2="9" />
        </svg>
      );
    case "ads":
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="10" />
          <circle cx="12" cy="12" r="6" />
          <circle cx="12" cy="12" r="2" />
        </svg>
      );
    case "influencer":
      return (
        <svg {...common}>
          <circle cx="18" cy="5" r="3" />
          <circle cx="6" cy="12" r="3" />
          <circle cx="18" cy="19" r="3" />
          <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
          <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
        </svg>
      );
    case "email":
      return (
        <svg {...common}>
          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
          <polyline points="22,6 12,13 2,6" />
        </svg>
      );
    default:
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
        </svg>
      );
  }
};

const fmt = (n) => Math.round(Number(n) || 0).toLocaleString("en-US");

// ---------- styled components ----------

const Page = styled.div`
  padding: 24px 40px 64px;
  max-width: 1320px;
  margin: 0 auto;
  font-family: ${FONT};
  height: calc(100vh - 59px);
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 10px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 27, 59, 0.28);
    border-radius: 5px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(0, 27, 59, 0.45);
  }
`;

// Header lays out title block on the left and a close affordance on
// the right. Matches the Manage Production page pattern so the close
// "x" behaves the same across decision overlays.
const HeaderBlock = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 22px;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  font-size: 24px;
  color: rgba(0, 27, 59, 0.6);
  cursor: pointer;
  padding: 4px 10px;
  line-height: 1;

  &:hover {
    color: rgba(0, 27, 59, 0.94);
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 4px;
`;

const TitleIcon = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: #DBEAFE;
  color: #2563EB;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
`;

const Title = styled.h1`
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: rgba(0, 27, 59, 0.94);
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: rgba(0, 27, 59, 0.6);
  line-height: 1.5;
  margin: 0;
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 28px;
`;

// Floating toast slot at the top of the overlay. The page is a
// scrollable column inside LenderOverlay; pinning the alert here keeps
// it visible regardless of scroll position.
const ToastSlot = styled.div`
  position: sticky;
  top: 0;
  z-index: 5;
  display: flex;
  justify-content: center;
  pointer-events: none;
  margin-bottom: -12px;

  & > * {
    pointer-events: auto;
  }
`;

const KpiCard = styled.div`
  background: #FFFFFF;
  border: 1px solid #E5E9F0;
  border-radius: 14px;
  padding: 20px 22px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 1px 2px rgba(15, 23, 41, 0.04);
`;

const KpiIcon = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
  background: #EAF3EC;
  color: #2DA548;
  font-weight: 700;
`;

const KpiContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const KpiLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  color: rgba(0, 27, 59, 0.6);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  line-height: 1.2;
  margin-bottom: 6px;
`;

const KpiValueRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
  flex-wrap: wrap;
`;

const KpiValue = styled.span`
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: rgba(0, 27, 59, 0.94);
`;

// Per-product breakdown chips laid out side-by-side, sitting closer
// to the center of the KPI card (not pinned to the far right) so the
// eye reads Total Spending → per-product split as a continuous group.
const SpendBreakdown = styled.div`
  margin: 0 auto;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 40px;
  flex-shrink: 0;
  flex-wrap: wrap;
  padding-right: 24px;
`;

const SpendBreakdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SpendDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${p => p.color};
`;

const SpendLabel = styled.span`
  font-size: 12px;
  color: rgba(0, 27, 59, 0.6);
  font-weight: 600;
`;

const SpendValue = styled.span`
  font-size: 14px;
  color: rgba(0, 27, 59, 0.94);
  font-weight: 800;
  font-variant-numeric: tabular-nums;
`;

// Header row above the channel grid — title on the left, action
// buttons on the right, vertically centered together so the user can
// see Reset All / Post Allocation without scrolling past the cards.
const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 16px;
  flex-wrap: wrap;
`;

const SectionTitle = styled.h2`
  font-size: 22px;
  font-weight: 800;
  letter-spacing: -0.01em;
  margin: 0;
  color: rgba(0, 27, 59, 0.94);
`;

// Title + product selector pills sit together on the left of the
// section header so the user always sees which product the channel
// cards are bound to.
const SectionTitleGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 28px;
  flex-wrap: wrap;
`;

const ProductPillsLabel = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: rgba(0, 27, 59, 0.6);
  letter-spacing: 0.04em;
  margin-right: 4px;
`;

const ProductPills = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

// Active pill: white background + navy border/text to read as "this is
// the product we're editing right now". Inactive: soft gray chip.
// Pulsing halo used when no product has been picked yet — visually
// nudges the user toward the pill they need to click to unlock the
// channel allocation grid below.
const pillPulse = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(58, 80, 123, 0.55); }
  70%  { box-shadow: 0 0 0 12px rgba(58, 80, 123, 0); }
  100% { box-shadow: 0 0 0 0 rgba(58, 80, 123, 0); }
`;

const ProductPill = styled.button`
  background: ${p => (p.active ? "#FFFFFF" : "#F5F7FB")};
  border: 1.5px solid ${p => (p.active ? "#3A507B" : "#E5E9F0")};
  color: ${p => (p.active ? "#3A507B" : "rgba(0, 27, 59, 0.6)")};
  padding: 7px 14px;
  border-radius: 999px;
  font-family: ${FONT};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: border-color 0.15s ease, color 0.15s ease, background 0.15s ease;
  box-shadow: ${p => (p.active ? "0 2px 6px rgba(58, 80, 123, 0.18)" : "none")};

  &:hover:not(:disabled) {
    border-color: #3A507B;
    color: rgba(0, 27, 59, 0.94);
  }

  ${p => p.attention && !p.active && css`
    background: #EEF3FB;
    border-color: #3A507B;
    color: #3A507B;
    animation: ${pillPulse} 1.6s ease-out infinite;
  `}

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ChannelsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 18px;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

// Empty state shown before the user picks a product. Sits in the same
// slot the channel grid would occupy so the layout doesn't jump when a
// pill is clicked.
const EmptyState = styled.div`
  border: 1.5px dashed #C6CDD8;
  background: #FBFCFE;
  border-radius: 14px;
  padding: 60px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 8px;
`;

const EmptyStateIcon = styled.div`
  font-size: 36px;
  line-height: 1;
  margin-bottom: 6px;
`;

const EmptyStateTitle = styled.div`
  font-family: ${FONT};
  font-weight: 700;
  font-size: 20px;
  line-height: 26px;
  color: rgba(0, 27, 59, 0.94);
`;

const EmptyStateBody = styled.div`
  font-family: ${FONT};
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  color: rgba(0, 27, 59, 0.6);
  max-width: 460px;
`;

const ChannelCard = styled.div`
  border: 1.5px solid ${p => (p.allocated ? "#3A507B" : "#E5E9F0")};
  background: #FFFFFF;
  border-radius: 14px;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
  box-shadow: ${p => (p.allocated
    ? "0 6px 20px rgba(58, 80, 123, 0.18)"
    : "none")};

  &:hover {
    box-shadow: ${p => (p.allocated
      ? "0 8px 24px rgba(58, 80, 123, 0.24)"
      : "0 4px 16px rgba(15, 23, 41, 0.06)")};
  }
`;

const ChannelHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
`;

const ChannelIconWrap = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  background: #F5F7FB;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(0, 27, 59, 0.6);
  flex-shrink: 0;
`;

const ChannelTitleBlock = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChannelName = styled.div`
  font-size: 16px;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 6px;
  color: rgba(0, 27, 59, 0.94);
  display: flex;
  align-items: center;
  gap: 6px;
`;

// Small "?" pill next to the channel name. Hovers reveal a tooltip
// with a plain-language explanation of what the channel is. Same
// disabled-child pointer-events trick used elsewhere so hover fires
// on the wrapper, not the icon, and the tooltip anchors cleanly.
const ChannelInfoWrapper = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  cursor: help;

  &:hover > span.channel-info-tooltip {
    opacity: 1;
    visibility: visible;
  }
`;

const ChannelInfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  border: 1.5px solid rgba(0, 27, 59, 0.35);
  color: rgba(0, 27, 59, 0.6);
  font-family: ${FONT};
  font-weight: 700;
  font-size: 11px;
  line-height: 1;
  user-select: none;
`;

const ChannelInfoTooltip = styled.span`
  position: absolute;
  top: 100%;
  left: 0;
  transform: translateY(8px);
  background: rgba(0, 27, 59, 0.94);
  color: #fff;
  font-family: ${FONT};
  font-weight: 400;
  font-size: 16px;
  line-height: 22px;
  padding: 10px 14px;
  border-radius: 6px;
  width: 280px;
  text-align: left;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s ease, visibility 0.15s ease;
  pointer-events: none;
  z-index: 20;

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 6px;
    border: 5px solid transparent;
    border-bottom-color: rgba(0, 27, 59, 0.94);
  }
`;

const ChannelBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.02em;
  border: 1.5px solid;
  ${p => p.category === CATEGORY_DIGITAL
    ? "background: #EBF2FF; color: #4C8AFF; border-color: #4C8AFF;"
    : "background: #ECF6F7; color: #4CC7D1; border-color: #4CC7D1;"}
`;

const ChannelDesc = styled.p`
  font-size: 13px;
  color: rgba(0, 27, 59, 0.6);
  line-height: 1.5;
  min-height: 40px;
  margin: 0;
`;

const AllocRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

// Custom div-based slider — native input[type=range] doesn't style
// reliably through styled-components v3 (vendor pseudo-elements stripped
// or overridden by the browser default skin). Building it from divs
// guarantees the design pixel-matches across browsers.
const SliderRoot = styled.div`
  flex: 1;
  position: relative;
  height: 20px;
  display: flex;
  align-items: center;
  cursor: ${p => (p.disabled ? "not-allowed" : "pointer")};
  opacity: ${p => (p.disabled ? 0.5 : 1)};
  touch-action: none;
  user-select: none;
  min-width: 0;
`;

const SliderTrack = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  height: 6px;
  background: #E5E9F0;
  border-radius: 999px;
  pointer-events: none;
`;

const SliderThumb = styled.div`
  position: absolute;
  width: 20px;
  height: 20px;
  background: #FFFFFF;
  border: 3px solid #FD9595;
  border-radius: 50%;
  box-shadow: 0 2px 6px rgba(253, 149, 149, 0.25);
  transform: translateX(-50%);
  top: 50%;
  margin-top: -10px;
  pointer-events: none;
  transition: transform 0.15s ease;

  ${SliderRoot}:hover & {
    transform: translateX(-50%) scale(1.1);
  }
`;

class CustomSlider extends Component {
  constructor(props) {
    super(props);
    this.state = { dragging: false };
  }

  componentWillUnmount() {
    this.detachWindowListeners();
  }

  setRootRef = (el) => {
    this.rootEl = el;
  };

  attachWindowListeners() {
    window.addEventListener("mousemove", this.onWindowMouseMove);
    window.addEventListener("mouseup", this.onWindowMouseUp);
    window.addEventListener("touchmove", this.onWindowTouchMove, { passive: false });
    window.addEventListener("touchend", this.onWindowMouseUp);
  }

  detachWindowListeners() {
    window.removeEventListener("mousemove", this.onWindowMouseMove);
    window.removeEventListener("mouseup", this.onWindowMouseUp);
    window.removeEventListener("touchmove", this.onWindowTouchMove);
    window.removeEventListener("touchend", this.onWindowMouseUp);
  }

  // Convert a clientX position to a clamped, stepped value, then push
  // it back up via props.onChange.
  updateFromClientX = (clientX) => {
    if (!this.rootEl || this.props.disabled) return;
    const rect = this.rootEl.getBoundingClientRect();
    if (rect.width <= 0) return;
    const ratio = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
    const { min, max, step, onChange } = this.props;
    let raw = min + (max - min) * ratio;
    if (step && step > 0) raw = Math.round(raw / step) * step;
    raw = Math.max(min, Math.min(max, raw));
    if (onChange) onChange(raw);
  };

  onMouseDown = (e) => {
    if (this.props.disabled) return;
    e.preventDefault();
    this.setState({ dragging: true });
    this.attachWindowListeners();
    this.updateFromClientX(e.clientX);
  };

  onTouchStart = (e) => {
    if (this.props.disabled) return;
    if (!e.touches || e.touches.length === 0) return;
    this.setState({ dragging: true });
    this.attachWindowListeners();
    this.updateFromClientX(e.touches[0].clientX);
  };

  onWindowMouseMove = (e) => {
    if (!this.state.dragging) return;
    this.updateFromClientX(e.clientX);
  };

  onWindowTouchMove = (e) => {
    if (!this.state.dragging) return;
    if (!e.touches || e.touches.length === 0) return;
    e.preventDefault();
    this.updateFromClientX(e.touches[0].clientX);
  };

  onWindowMouseUp = () => {
    if (!this.state.dragging) return;
    this.setState({ dragging: false });
    this.detachWindowListeners();
  };

  render() {
    const { value, min, max, disabled } = this.props;
    const range = max - min;
    const pct = range > 0
      ? Math.max(0, Math.min(100, ((Number(value) - min) / range) * 100))
      : 0;
    return (
      <SliderRoot
        innerRef={this.setRootRef}
        onMouseDown={this.onMouseDown}
        onTouchStart={this.onTouchStart}
        disabled={disabled}
      >
        <SliderTrack />
        <SliderThumb style={{ left: `${pct}%` }} />
      </SliderRoot>
    );
  }
}

// Wrap NumberFormat (react-number-format v2) inside a styled container.
// Styling the NumberFormat-rendered <input> directly via descendant
// selector is more reliable across the v2 component than wrapping it
// with styled() — v2's class component doesn't always forward className.
const InputWrap = styled.div`
  display: flex;
  align-items: center;
  background: #F5F7FB;
  border: 1.5px solid #E5E9F0;
  border-radius: 10px;
  padding: 8px 12px;
  width: 120px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus-within {
    border-color: #EA580C;
    box-shadow: 0 0 0 3px rgba(234, 88, 12, 0.12);
    background: #FFFFFF;
  }

  input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-family: ${FONT};
    font-size: 15px;
    font-weight: 800;
    color: rgba(0, 27, 59, 0.94);
    text-align: right;
    font-variant-numeric: tabular-nums;
    width: 100%;
    min-width: 0;
    padding: 0;
  }
`;

const InputPrefix = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: rgba(0, 27, 59, 0.35);
  margin-right: 6px;
`;

// Stacks the product name above the dollar input so every channel
// card makes it visually clear which product the allocation belongs to.
// Left-aligned (not flex-end) so "Product X" reads naturally from the
// start of the input area.
const InputColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  min-width: 120px;
`;

const InputProductLabel = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: #3A507B;
  letter-spacing: 0.02em;
  max-width: 120px;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const FooterButtons = styled.div`
  display: flex;
  gap: 12px;
`;

const BtnSecondary = styled.button`
  background: transparent;
  border: 1.5px solid #E5E9F0;
  color: rgba(0, 27, 59, 0.6);
  padding: 12px 22px;
  border-radius: 12px;
  font-family: ${FONT};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  &:hover:not(:disabled) {
    border-color: rgba(0, 27, 59, 0.6);
    color: rgba(0, 27, 59, 0.94);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const BtnPrimary = styled.button`
  background: #3A507B;
  color: white;
  border: none;
  padding: 14px 28px;
  border-radius: 12px;
  font-family: ${FONT};
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s ease, transform 0.15s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(58, 80, 123, 0.25);

  &:hover:not(:disabled) {
    background: #2E3F62;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

// ---------- $0-promo confirmation modal ----------
//
// Fires when the user tries to Set Allocation while at least one
// released product still has $0 total promo spend across every
// channel. Two buttons: Cancel returns them to the sliders; Continue
// commits the save with the un-promoted products intact.

const ConfirmBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  z-index: 1200;
`;

const ConfirmCard = styled.div`
  background: #ffffff;
  border-radius: 12px;
  width: 100%;
  max-width: 460px;
  padding: 28px 28px 24px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.25);
  font-family: 'Mukta Vaani', Lato, sans-serif;
  color: rgba(0, 27, 59, 0.94);
`;

const ConfirmTitle = styled.h2`
  margin: 0 0 12px;
  font-size: 20px;
  font-weight: 800;
`;

const ConfirmBody = styled.div`
  font-size: 16px;
  line-height: 1.55;
  margin-bottom: 20px;
`;

const ConfirmProductList = styled.ul`
  margin: 8px 0 0;
  padding-left: 20px;
  font-weight: 700;
`;

const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const ConfirmSecondaryBtn = styled.button`
  padding: 10px 20px;
  border: 1.5px solid #D5D9DD;
  background: #ffffff;
  border-radius: 8px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 700;
  font-size: 15px;
  color: rgba(0, 27, 59, 0.94);
  cursor: pointer;
`;

const ConfirmPrimaryBtn = styled.button`
  padding: 10px 20px;
  border: 0;
  background: #2563EB;
  border-radius: 8px;
  font-family: 'Mukta Vaani', Lato, sans-serif;
  font-weight: 700;
  font-size: 15px;
  color: #ffffff;
  cursor: pointer;
`;

// ---------- component ----------

class ManagePromotionPage extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      // Allocations are keyed first by product id, then by client id.
      // Switching the selected product just changes which inner map is
      // wired to the sliders/inputs; edits on a previously-viewed
      // product stay buffered until the user posts that product.
      allocations: {},
      selectedProductId: null,
      isSaving: false,
      errorMessage: null,
      successMessage: null,
      // Names of released products with $0 total promo spend. When
      // non-null, the "continue anyway?" confirmation modal renders
      // and the actual save is deferred until the user picks a button.
      noPromoProductNames: null,
      // "No allocation at all" soft warn. True when the CEO clicks
      // Set Allocation without spending on any channel of any product.
      // Was previously a hard block; now surfaces the same confirm
      // modal pattern so the user can proceed with an empty post.
      confirmEmptyPost: false,
    };
    this._successTimer = null;
    this.eventemitter = EventEmitter.getInstance();
  }

  showPopupLowPromo = (spend) => {
    const formatted = Math.round(Number(spend) || 0).toLocaleString("en-US");
    this.eventemitter.trigger("coaching-feedback", [
      "Food for Thought",
      <Fragment>
        <b>Uh Oh! Very low promo!</b><br />
        You should double check the <a href="/analysis/bcase" target="_blank">business case</a> to see what others have spent on their marketing budget.
      </Fragment>,
      <Fragment>
        You don't have to spend the same amount as others, but it's in your best interests to spend more than ${formatted} if this is the first time promoting this product.
      </Fragment>,
    ]);
  };

  componentDidMount() {
    this._mounted = true;
    this.initAllocations(this.props);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.promos !== this.props.promos
        || nextProps.marketing_clients !== this.props.marketing_clients
        || nextProps.products !== this.props.products) {
      // Reload allocations from props but keep the current
      // selectedProductId — resetting on reload would jump the user
      // back to the empty state right after they save.
      this.initAllocations(nextProps);
    }
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this._successTimer) {
      clearTimeout(this._successTimer);
      this._successTimer = null;
    }
  }

  // Local in-overlay success toast. We can't rely on the parent's
  // SuccessAlert because it renders behind LenderOverlay (z-index 1500),
  // so a confirmation here is invisible until the user closes the page.
  showLocalSuccess = (message) => {
    if (this._successTimer) clearTimeout(this._successTimer);
    this.setState({ successMessage: message });
    this._successTimer = setTimeout(() => {
      if (!this._mounted) return;
      this.setState({ successMessage: null });
    }, 5000);
  };

  // Build a per-product map of per-client allocations from the promos
  // array. We only treat a promo as a live decision when player !== null
  // (matches the convention in the rest of the team-decisions code).
  // Each product gets its own inner map so switching product selection
  // simply swaps which inner map drives the sliders.
  initAllocations = (props) => {
    const allocations = {};
    (props.products || []).forEach(product => {
      const inner = {};
      (props.marketing_clients || []).forEach(client => {
        const spend = (props.promos || [])
          .filter(p => p.player !== null
            && Number(p.product) === Number(product.id)
            && Number(p.client) === Number(client.id))
          .reduce((s, p) => s + (Number(p.spend) || 0), 0);
        inner[client.id] = Math.abs(spend);
      });
      allocations[product.id] = inner;
    });

    // Intentionally leave selectedProductId as null on first load and on
    // reloads. The Channel Allocation grid is per-product and it should
    // be an explicit choice — otherwise the user misses that the sliders
    // they're editing only apply to one product at a time.
    this.setState({ allocations });
  };

  selectProduct = (productId) => () => {
    if (Number(this.state.selectedProductId) === Number(productId)) return;
    this.setState({ selectedProductId: productId });
  };

  setAllocation = (clientId, value) => {
    const { selectedProductId, allocations } = this.state;
    if (selectedProductId == null) return;
    const clamped = Math.max(0, Math.min(PER_CHANNEL_MAX, Number(value) || 0));
    this.setState({
      allocations: {
        ...allocations,
        [selectedProductId]: {
          ...(allocations[selectedProductId] || {}),
          [clientId]: clamped,
        },
      },
    });
  };

  onSliderChange = (clientId) => (value) => {
    this.setAllocation(clientId, value);
  };

  onInputChange = (clientId) => (e, values) => {
    const next = (values && typeof values.floatValue === "number")
      ? values.floatValue
      : 0;
    this.setAllocation(clientId, next);
  };

  // Reset only the currently-selected product back to zeros — other
  // products keep their buffered edits intact.
  resetAll = () => {
    const { selectedProductId, allocations } = this.state;
    if (selectedProductId == null) return;
    const inner = {};
    (this.props.marketing_clients || []).forEach(client => {
      inner[client.id] = 0;
    });
    this.setState({
      allocations: {
        ...allocations,
        [selectedProductId]: inner,
      },
    });
  };

  // ---------- totals ----------

  // Total promo spend across every product the team owns (sum of every
  // buffered allocation map). Drives the headline KPI value.
  get totalSpent() {
    const { allocations } = this.state;
    return Object.keys(allocations).reduce((sum, productId) => {
      const inner = allocations[productId] || {};
      return sum + Object.keys(inner).reduce(
        (s, clientId) => s + (Number(inner[clientId]) || 0),
        0
      );
    }, 0);
  }

  // Total spend for a single product across all channels. Drives the
  // per-product chips in the KPI breakdown row.
  spentByProduct = (productId) => {
    const inner = (this.state.allocations || {})[productId] || {};
    return Object.keys(inner).reduce(
      (s, clientId) => s + (Number(inner[clientId]) || 0),
      0
    );
  };

  // Allocation value for a (selectedProduct, client) pair. Returns 0
  // when the inner map hasn't been populated yet.
  currentAllocation = (clientId) => {
    const { selectedProductId, allocations } = this.state;
    if (selectedProductId == null) return 0;
    const inner = allocations[selectedProductId] || {};
    return Number(inner[clientId]) || 0;
  };

  // ---------- save ----------

  hideErrorMessage = () => {
    this.setState({ errorMessage: null });
  };

  // The bulk endpoint is scoped to (team, product, round). team and
  // player come from auth/Teams; round id is looked up from the rounds
  // array. Each product is sent as its own bulk request.
  resolveBulkContext = () => {
    const { rounds = [], currentRound } = this.props;
    const teamId = Auth.getTeamId();
    const player = Teams.currentPlayer();
    const playerId = player ? player.id : null;
    const round = rounds.find(r => Number(r.number) === Number(currentRound));
    const roundId = round ? round.id : null;
    return { teamId, playerId, roundId };
  };

  postAllocation = () => {
    if (!this.props.actionsAllowed) return;
    const { marketing_clients, products = [] } = this.props;
    const { allocations } = this.state;

    if (products.length === 0) {
      this.setState({
        errorMessage: "There are no products to allocate promo spending to.",
      });
      return;
    }

    // Validation: at least ONE product must have at least one channel
    // with spending > 0. With zero everywhere there's nothing
    // meaningful to post — the user likely clicked by mistake.
    const hasAnySpend = products.some(product => {
      const inner = (allocations || {})[product.id] || {};
      return (marketing_clients || []).some(
        client => (Number(inner[client.id]) || 0) > 0
      );
    });
    if (!hasAnySpend) {
      this.setState({ confirmEmptyPost: true });
      return;
    }

    // Soft-warn on released products that still have $0 total promo
    // spend (nothing invested across any channel). Missing
    // months_available_to_produce is treated as "released" per the
    // game rule so we don't quietly let an un-promoted product slip
    // through when the field is absent.
    const noPromoReleasedNames = products.filter(product => {
      const inner = (allocations || {})[product.id] || {};
      const totalSpend = (marketing_clients || []).reduce(
        (sum, client) => sum + (Number(inner[client.id]) || 0),
        0
      );
      if (totalSpend > 0) return false;
      const months = product.months_available_to_produce != null
        ? Number(product.months_available_to_produce)
        : 1;
      return months > 0;
    }).map(p => p.name);
    if (noPromoReleasedNames.length > 0) {
      this.setState({ noPromoProductNames: noPromoReleasedNames });
      return;
    }

    this.doPostAllocation();
  };

  confirmNoPromoPost = () => {
    this.setState({ noPromoProductNames: null });
    this.doPostAllocation();
  };

  cancelNoPromoPost = () => {
    this.setState({ noPromoProductNames: null });
  };

  confirmEmptyPostContinue = () => {
    this.setState({ confirmEmptyPost: false });
    this.doPostAllocation();
  };

  cancelEmptyPost = () => {
    this.setState({ confirmEmptyPost: false });
  };

  doPostAllocation = () => {
    const { marketing_clients, products = [] } = this.props;
    const { allocations } = this.state;
    const { teamId, playerId, roundId } = this.resolveBulkContext();
    if (!teamId || !playerId || !roundId) {
      this.setState({
        errorMessage: "We couldn't determine your team, round, or player context. Please refresh the page and try again.",
      });
      return;
    }

    // Capture BEFORE the POST: is this Round 1 AND the team's first
    // promo record ever? Used to fire the "one more marketing
    // decision to go" coaching popup on the very first Set Allocation.
    const wasFirstPromoRd1 =
      Number(this.props.currentRound) === 1
      && !(this.props.promos || []).some(p => p && p.player !== null);

    // One bulk PUT per product, fired in parallel. Each request sends
    // the full spends array (with explicit 0s) so the bulk endpoint
    // can upsert/delete each channel independently per product.
    const requests = products.map(product => {
      const inner = (allocations || {})[product.id] || {};
      const spends = (marketing_clients || []).map(client => ({
        client: client.id,
        spend: Math.round(Number(inner[client.id]) || 0),
      }));
      const body = {
        team: teamId,
        round: roundId,
        player: playerId,
        product: product.id,
        spends,
      };
      return putRequest(PROMOS_BULK_URL).send(body);
    });

    this.setState({ isSaving: true });

    Promise.all(requests)
      .then(() => {
        // Coaching popup: if ANY product's total promo spend (across
        // both Traditional and Digital channels) is <= $200k, nudge
        // the student to review the business case. Uses the minimum
        // across products so a well-funded product doesn't mask an
        // under-invested one.
        const clients = marketing_clients || [];
        let minPromoPerProduct = null;
        products.forEach(product => {
          const inner = (allocations || {})[product.id] || {};
          const totalPromo = clients.reduce(
            (sum, c) => sum + (Number(inner[c.id]) || 0),
            0
          );
          if (minPromoPerProduct === null || totalPromo < minPromoPerProduct) {
            minPromoPerProduct = totalPromo;
          }
        });
        if (minPromoPerProduct !== null && minPromoPerProduct <= 200000) {
          this.showPopupLowPromo(minPromoPerProduct);
        }

        incrementUnacceptedDecisions("marketing");
        // reloadData already refreshes team-decisions + team-status +
        // submission_data in one go; calling getSubmissionData on top
        // would duplicate both network requests for no extra state.
        if (this.props.reloadData) this.props.reloadData();
        if (this._mounted) {
          this.setState({ isSaving: false });
          this.showLocalSuccess("Promo allocations were successfully saved.");
        }
        if (wasFirstPromoRd1 && this.props.onFirstPromoSet) {
          this.props.onFirstPromoSet();
        }
      })
      .catch(err => {
        logError(err, "ManagePromotionPage", "postAllocation");
        if (this._mounted) {
          this.setState({
            isSaving: false,
            errorMessage: "Something went wrong saving your promo allocations. Please try again.",
          });
        }
      });
  };

  // ---------- render ----------

  renderChannelCard = (client) => {
    const { actionsAllowed, products = [] } = this.props;
    const { selectedProductId } = this.state;
    const value = this.currentAllocation(client.id);
    const meta = CHANNEL_META[client.name] || { description: client.name, iconKey: "default" };
    const selectedProduct = products.find(p => Number(p.id) === Number(selectedProductId));
    const productName = selectedProduct ? selectedProduct.name : null;

    return (
      <ChannelCard key={client.id} allocated={value > 0}>
        <ChannelHeader>
          <ChannelIconWrap>{renderChannelIcon(meta.iconKey)}</ChannelIconWrap>
          <ChannelTitleBlock>
            <ChannelName>
              <span>{client.name}</span>
              {meta.explainer && (
                <ChannelInfoWrapper>
                  <ChannelInfoIcon aria-label={`About ${client.name}`}>?</ChannelInfoIcon>
                  <ChannelInfoTooltip className="channel-info-tooltip">
                    {meta.explainer}
                  </ChannelInfoTooltip>
                </ChannelInfoWrapper>
              )}
            </ChannelName>
            <ChannelBadge category={Number(client.category)}>
              {Number(client.category) === CATEGORY_DIGITAL ? "Digital" : "Traditional"}
            </ChannelBadge>
          </ChannelTitleBlock>
        </ChannelHeader>
        <ChannelDesc>{meta.description}</ChannelDesc>
        <AllocRow>
          <CustomSlider
            min={0}
            max={PER_CHANNEL_MAX}
            step={SLIDER_STEP}
            value={value}
            onChange={this.onSliderChange(client.id)}
            disabled={!actionsAllowed || selectedProductId == null}
          />
          <InputColumn>
            {productName && (
              <InputProductLabel title={`Product ${productName}`}>
                Product {productName}
              </InputProductLabel>
            )}
            <InputWrap>
              <InputPrefix>$</InputPrefix>
              <NumberFormat
                value={value}
                thousandSeparator=","
                allowNegative={false}
                onChange={this.onInputChange(client.id)}
                disabled={!actionsAllowed || selectedProductId == null}
              />
            </InputWrap>
          </InputColumn>
        </AllocRow>
      </ChannelCard>
    );
  };

  render() {
    const { marketing_clients = [], products = [], promos = [], actionsAllowed, onClose } = this.props;
    const {
      isSaving, errorMessage, successMessage, selectedProductId, noPromoProductNames, confirmEmptyPost,
    } = this.state;

    const totalSpent = this.totalSpent;
    // Only nudge with the pulsing glow on the very first visit — once
    // any promo record has been posted (any product), the user has
    // discovered the pill affordance and the glow becomes noisy.
    const hasAnyPromoDecision = (promos || []).some(p => p.player !== null);

    return (
      <FinanceStyled.LenderOverlay>
        {errorMessage && (
          <ErrorAlert
            title="Heads up"
            message={errorMessage}
            onHide={this.hideErrorMessage}
          />
        )}
        {confirmEmptyPost && (
          <ConfirmBackdrop role="dialog" aria-modal="true">
            <ConfirmCard>
              <ConfirmTitle>Continue with no promo spend?</ConfirmTitle>
              <ConfirmBody>
                You haven't allocated any spending across your channels.
                Without promo spend, customers may not hear about your
                products. Go back to allocate spending, or continue anyway.
              </ConfirmBody>
              <ConfirmActions>
                <ConfirmSecondaryBtn onClick={this.cancelEmptyPost}>
                  Go Back
                </ConfirmSecondaryBtn>
                <ConfirmPrimaryBtn onClick={this.confirmEmptyPostContinue}>
                  Continue
                </ConfirmPrimaryBtn>
              </ConfirmActions>
            </ConfirmCard>
          </ConfirmBackdrop>
        )}
        {noPromoProductNames && noPromoProductNames.length > 0 && (
          <ConfirmBackdrop role="dialog" aria-modal="true">
            <ConfirmCard>
              <ConfirmTitle>Continue with no promo spend?</ConfirmTitle>
              <ConfirmBody>
                {noPromoProductNames.length === 1
                  ? "This released product has no marketing spend across any channel:"
                  : "These released products have no marketing spend across any channel:"}
                <ConfirmProductList>
                  {noPromoProductNames.map(name => (
                    <li key={name}>{name}</li>
                  ))}
                </ConfirmProductList>
                <div style={{ marginTop: 12 }}>
                  Without promo spend, customers may not hear about
                  {noPromoProductNames.length === 1 ? " this product." : " these products."}
                  {" "}Go back to allocate spending, or continue anyway.
                </div>
              </ConfirmBody>
              <ConfirmActions>
                <ConfirmSecondaryBtn onClick={this.cancelNoPromoPost}>
                  Go Back
                </ConfirmSecondaryBtn>
                <ConfirmPrimaryBtn onClick={this.confirmNoPromoPost}>
                  Continue
                </ConfirmPrimaryBtn>
              </ConfirmActions>
            </ConfirmCard>
          </ConfirmBackdrop>
        )}
        <Page>
          {successMessage && (
            <ToastSlot>
              <SuccessAlert message={successMessage} />
            </ToastSlot>
          )}
          <HeaderBlock>
            <HeaderLeft>
              <TitleRow>
                <TitleIcon>📣</TitleIcon>
                <Title>Manage Promotion</Title>
              </TitleRow>
            </HeaderLeft>
            <CloseButton onClick={onClose} aria-label="Close">×</CloseButton>
          </HeaderBlock>

          <KpiRow>
            <KpiCard>
              <KpiIcon>$</KpiIcon>
              <KpiContent>
                <KpiLabel>Total Promo Spending</KpiLabel>
                <KpiValueRow>
                  <KpiValue>${fmt(totalSpent)}</KpiValue>
                </KpiValueRow>
              </KpiContent>
              <SpendBreakdown>
                {products.map(product => (
                  <SpendBreakdownItem key={`product-spend-${product.id}`}>
                    <SpendDot color="#3A507B" />
                    <SpendLabel>Product {product.name}</SpendLabel>
                    <SpendValue>${fmt(this.spentByProduct(product.id))}</SpendValue>
                  </SpendBreakdownItem>
                ))}
              </SpendBreakdown>
            </KpiCard>
          </KpiRow>

          <SectionHeader>
            <SectionTitleGroup>
              <SectionTitle>Channel Allocation</SectionTitle>
              {products.length > 0 && (
                <ProductPills>
                  <ProductPillsLabel>Products:</ProductPillsLabel>
                  {products.map(product => (
                    <ProductPill
                      key={`pill-${product.id}`}
                      active={Number(product.id) === Number(selectedProductId)}
                      attention={selectedProductId == null && !hasAnyPromoDecision}
                      onClick={this.selectProduct(product.id)}
                      type="button"
                    >
                      {product.name}
                    </ProductPill>
                  ))}
                </ProductPills>
              )}
            </SectionTitleGroup>
            <FooterButtons>
              <BtnSecondary
                onClick={this.resetAll}
                disabled={!actionsAllowed || isSaving || selectedProductId == null}
              >
                Reset All
              </BtnSecondary>
              <BtnPrimary
                onClick={this.postAllocation}
                disabled={!actionsAllowed || isSaving || selectedProductId == null}
              >
                <span>{isSaving ? "Saving…" : "Set Allocation"}</span>
                {!isSaving && <span>→</span>}
              </BtnPrimary>
            </FooterButtons>
          </SectionHeader>

          {selectedProductId != null && (
            <Subtitle style={{ marginTop: 4, marginBottom: 12 }}>
              These are your marketing channel options. Invest in the ones best suited to reach your customers.
            </Subtitle>
          )}

          {selectedProductId == null ? (
            <EmptyState>
              <EmptyStateIcon>👆</EmptyStateIcon>
              <EmptyStateTitle>Select a product to begin</EmptyStateTitle>
              <EmptyStateBody>
                Channel allocations are set per product. Pick a product above to
                view channel allocation options.
              </EmptyStateBody>
            </EmptyState>
          ) : (
            <ChannelsGrid>
              {marketing_clients.map(this.renderChannelCard)}
            </ChannelsGrid>
          )}
        </Page>
      </FinanceStyled.LenderOverlay>
    );
  }
}

export default ManagePromotionPage;



// WEBPACK FOOTER //
// ./src/components/pm/decisions/manage-promotion-page.js